import React, { useState } from 'react';
import { Shield, ArrowLeft, Check, X, Lock, Eye, Users, FileText, Clock, Globe, AlertCircle } from 'lucide-react';

const PrivacyPolicy = () => {
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const [optOutForm, setOptOutForm] = useState({
    email: '',
    fullName: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');

  const handleOptOutSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setOptOutForm({ email: '', fullName: '', reason: '' });
      
      setTimeout(() => {
        setShowOptOutModal(false);
        setSubmitStatus('idle');
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturnHome = () => {
    // Replace with your actual navigation logic
    window.location.href = '/#home';
  };

  const handleContactClick = () => {
    // Replace with your actual navigation logic
    window.location.href = '/#contact';
  };

  const OptOutModal = () => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                <X className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Do Not Sell My Information</h3>
                <p className="text-sm text-gray-500">CCPA/CPRA Rights Request</p>
              </div>
            </div>
            <button
              onClick={() => setShowOptOutModal(false)}
              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Request Submitted Successfully</h4>
              <p className="text-gray-600 leading-relaxed">
                We'll process your opt-out request within 15 business days as required by CCPA/CPRA.
                Your data will be flagged in our systems and excluded from personalized advertising, 
                behavioral analytics, and third-party data sharing not essential to our services.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={optOutForm.email}
                  onChange={(e) => setOptOutForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={optOutForm.fullName}
                  onChange={(e) => setOptOutForm(prev => ({ ...prev, fullName: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={optOutForm.reason}
                  onChange={(e) => setOptOutForm(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  rows={4}
                  placeholder="Tell us why you're opting out (optional)"
                />
              </div>

              {submitStatus === 'error' && (
                <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-red-700 text-sm">
                    Failed to submit request. Please try again or contact us directly.
                  </span>
                </div>
              )}

              <div className="flex space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => setShowOptOutModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleOptOutSubmit}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const Section = ({ icon: Icon, title, children, className = "" }) => (
    <section className={`mb-12 ${className}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="pl-13">
        {children}
      </div>
    </section>
  );

  const SubSection = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );

  const InfoCard = ({ icon: Icon, title, description, action }) => (
    <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 mb-4 leading-relaxed">{description}</p>
          {action}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button 
            onClick={handleReturnHome}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Return to Homepage</span>
          </button>
          
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                <span>Last Updated: June 28, 2025</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Introduction */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Privacy Matters</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            At Manifest Illusions, we respect your privacy and are committed to protecting your personal information. 
            This Privacy Policy explains how we collect, use, share, and protect your information when you use our 
            website and services. We believe in transparency and want you to understand exactly how your data is handled.
          </p>
        </div>

        {/* Information Collection */}
        <Section icon={Eye} title="Information We Collect">
          <SubSection title="Information You Provide Directly">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Name, email address, and payment information (processed securely by Stripe/PayPal)</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Files, designs, or data you upload to our platform for design services</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Communications and feedback you send to us</span>
              </li>
            </ul>
          </SubSection>

          <SubSection title="Information Collected Automatically">
            <p className="text-gray-700 mb-3">Our third-party services may automatically collect:</p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>IP address and approximate location (city, country)</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Browser type, device information, and operating system</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Session data including login times and usage patterns</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Cookies and similar tracking technologies</span>
              </li>
            </ul>
          </SubSection>

          <SubSection title="Payment Information">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Lock className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">Secure Payment Processing</span>
              </div>
              <p className="text-green-700">
                We use industry-leading payment processors (Stripe and PayPal) to handle all transactions. 
                We do not store your credit card information directly on our servers.
              </p>
            </div>
          </SubSection>
        </Section>

        {/* How We Use Information */}
        <Section icon={FileText} title="How We Use Your Information">
          <p className="text-gray-700 mb-6">We use the information we collect to provide you with the best possible experience:</p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Provide and deliver our design services",
              "Authenticate and manage user accounts",
              "Process payments and fulfill transactions",
              "Communicate with you about our services",
              "Analyze trends and optimize performance",
              "Ensure platform security and prevent fraud",
              "Comply with legal obligations",
              "Improve our services and user experience"
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Information Sharing */}
        <Section icon={Users} title="Information Sharing">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-800">We Do Not Sell Your Personal Data</span>
            </div>
            <p className="text-blue-700">
              Your privacy is important to us. We never sell your personal information to third parties.
            </p>
          </div>

          <p className="text-gray-700 mb-4">We may share your information only in these limited circumstances:</p>
          <ul className="space-y-4">
            <li className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <Globe className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <span className="font-medium text-gray-900">Service Providers:</span>
                <span className="text-gray-700 ml-1">Trusted partners like Supabase, Netlify, Stripe, and Google Cloud who help us operate our platform</span>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <FileText className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <span className="font-medium text-gray-900">Legal Requirements:</span>
                <span className="text-gray-700 ml-1">When required by law or to protect our rights and safety</span>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <Users className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <span className="font-medium text-gray-900">Business Transfers:</span>
                <span className="text-gray-700 ml-1">In case of a merger, acquisition, or sale of our business</span>
              </div>
            </li>
          </ul>
        </Section>

        {/* Cookies & Tracking */}
        <Section icon={Globe} title="Cookies & Tracking Technologies">
          <p className="text-gray-700 mb-4">We use cookies and similar technologies to enhance your experience:</p>
          <div className="space-y-4">
            {[
              { name: "Essential Cookies", desc: "Required for authentication and basic website functionality" },
              { name: "Analytics Cookies", desc: "Help us understand how visitors use our website (via Google Analytics)" },
              { name: "Performance Cookies", desc: "Allow us to improve website performance and user experience" }
            ].map((cookie, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-1">{cookie.name}</h4>
                <p className="text-gray-600 text-sm">{cookie.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-sm mt-4">
            You can control cookies through your browser settings, but some features may not function correctly if disabled.
          </p>
        </Section>

        {/* Data Security */}
        <Section icon={Lock} title="Data Security & Protection">
          <div className="bg-green-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-green-800 mb-3">Industry-Standard Security Measures</h3>
            <div className="space-y-3">
              {[
                "End-to-end encryption for data transmission",
                "Secure cloud infrastructure with regular backups",
                "Regular security audits and vulnerability assessments",
                "Access controls and authentication protocols",
                "Compliance with industry security standards"
              ].map((measure, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-700 text-sm">{measure}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-gray-600 text-sm">
            While we implement robust security measures, no system is 100% secure. Please use strong passwords 
            and log out when finished using our services.
          </p>
        </Section>

        {/* Data Retention */}
        <Section icon={Clock} title="Data Retention">
          <p className="text-gray-700 mb-4">
            We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy, 
            including legal, operational, and security obligations.
          </p>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Retention Periods:</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Account data: Retained while your account is active</li>
              <li>• Transaction records: 7 years for tax and legal compliance</li>
              <li>• Support communications: 3 years for quality assurance</li>
              <li>• Analytics data: Anonymized after 26 months</li>
            </ul>
          </div>
        </Section>

        {/* Legal Basis (EU/EEA) */}
        <Section icon={FileText} title="Legal Basis for Processing (EU/EEA)">
          <p className="text-gray-700 mb-4">
            If you are located in the EU or EEA, we process your personal data under these legal bases:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { basis: "Contract Performance", desc: "Delivering services you've purchased" },
              { basis: "Legitimate Interest", desc: "Site functionality and fraud prevention" },
              { basis: "Consent", desc: "Email marketing and optional features" },
              { basis: "Legal Obligation", desc: "Compliance with applicable laws" }
            ].map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-1">{item.basis}</h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Your Rights */}
        <Section icon={Shield} title="Your Privacy Rights">
          <div className="grid md:grid-cols-2 gap-6">
            <InfoCard
              icon={Eye}
              title="Access Your Data"
              description="Request a copy of all personal information we have about you."
              action={
                <button 
                  onClick={handleContactClick}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                >
                  Request Data Access →
                </button>
              }
            />
            <InfoCard
              icon={FileText}
              title="Correct Information"
              description="Update or correct any inaccurate personal information."
              action={
                <button 
                  onClick={handleContactClick}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                >
                  Request Correction →
                </button>
              }
            />
            <InfoCard
              icon={X}
              title="Delete Your Data"
              description="Request deletion of your personal information (subject to legal requirements)."
              action={
                <button 
                  onClick={handleContactClick}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                >
                  Request Deletion →
                </button>
              }
            />
            <InfoCard
              icon={Users}
              title="Opt-Out of Sharing"
              description="California residents can opt-out of data sharing for advertising purposes."
              action={
                <button 
                  onClick={() => setShowOptOutModal(true)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-medium text-sm transition-colors"
                >
                  Do Not Sell My Info
                </button>
              }
            />
          </div>
        </Section>

        {/* Do Not Track */}
        <Section icon={Globe} title="Do Not Track & Opt-Out Policies">
          <div className="space-y-4">
            <p className="text-gray-700">
              We do not currently respond to "Do Not Track" browser signals, as there is no uniform standard 
              for handling such requests.
            </p>
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">California Residents (CCPA/CPRA)</h4>
              <p className="text-blue-700 text-sm">
                We respond to verified opt-out requests within 15 business days as required by law. 
                Your request will be processed to exclude you from data sharing for advertising purposes.
              </p>
            </div>
          </div>
        </Section>

        {/* Children's Privacy */}
        <Section icon={Shield} title="Children's Privacy">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-orange-800">Age Restriction</span>
            </div>
            <p className="text-orange-700">
              Our services are not intended for children under 13. We do not knowingly collect personal 
              information from children. If you believe we have collected information from a child, 
              please contact us immediately.
            </p>
          </div>
        </Section>

        {/* Policy Changes */}
        <Section icon={Clock} title="Changes to This Policy">
          <p className="text-gray-700 mb-4">
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
            When we make significant changes, we will notify you by:
          </p>
          <ul className="space-y-2 mb-4">
            <li className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-gray-700">Posting the updated policy on our website</span>
            </li>
            <li className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-gray-700">Sending email notifications for material changes</span>
            </li>
          </ul>
          <p className="text-gray-600 text-sm">
            The most recent version will always be available at ManifestIllusions.com/privacy
          </p>
        </Section>

        {/* Contact Information */}
        <Section icon={Users} title="Contact Us">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Questions About Your Privacy?</h3>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or how we handle your personal information, 
              we're here to help.
            </p>
            <button 
              onClick={handleContactClick}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Contact Our Privacy Team
            </button>
          </div>
        </Section>

        {/* California Privacy Rights - Prominent Section */}
        <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-8 mt-12">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <X className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">California Privacy Rights (CCPA/CPRA)</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                California residents have the right to opt-out of the sale or sharing of personal information 
                for targeted advertising purposes. Exercise your right to control how your data is used.
              </p>
              <button
                onClick={() => setShowOptOutModal(true)}
                className="bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                Do Not Sell My Information
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Opt-Out Modal */}
      {showOptOutModal && <OptOutModal />}
    </div>
  );
};

export default PrivacyPolicy;